/*
  Sale events (sections/sale-*.liquid, snippets/sale-visibility.liquid)

  Liquid writes each sale event's start and finish into the page as seconds since 1970,
  from the event's entry in Content > Metaobjects > Sale event. But Liquid only knows the
  moment the page was rendered, and Shopify can serve that page from its cache for a
  while, so this keeps the page in step with the clock, once a second:
  - [data-sale-from] / [data-sale-to]: shown inside that window, hidden outside it, so
    content scheduled for a phase appears and disappears on the second, no reload;
  - [data-sale-gate] (the Sale schedule section): data-sale-hide moves instead, and that
    section's CSS hides the sections below it;
  - [data-sale-countdown]: the digits tick and the label follows the phase;
  - template[data-sale-pending]: content Liquid left out because its window was more than
    two hours off. When the window opens the page reloads to fetch it, once per window
    per tab, so a stale cache serving the same page again can't loop.

  In the theme editor it adds a "Sale preview" bar that moves the clock to before, during
  or after each event on the page, and shows a scheduled block or section while it is
  selected, so every phase can be seen and edited. Loaded by every sale section; the
  guard below keeps it to one copy.
*/
(() => {
  if (window.saleEvents) return;

  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const PREVIEW_KEY = 'sale-events:preview';
  const HOUR = 3600000;
  let offset = 0; // How far the editor preview has moved the clock, in ms.
  let reloading = false;

  const now = () => Date.now() + offset;
  const ms = (seconds) => (seconds === undefined || seconds === '' ? null : Number(seconds) * 1000);
  const pad = (value) => String(value).padStart(2, '0');

  function isOpen(element, time) {
    const from = ms(element.dataset.saleFrom);
    const to = ms(element.dataset.saleTo);
    // Windows are whole seconds, and the last second still counts.
    return (from === null || time >= from) && (to === null || time < to + 1000);
  }

  function tick(countdown, time) {
    const start = ms(countdown.dataset.start);
    const end = ms(countdown.dataset.end);
    if (start === null || end === null) return;

    let phase = 'live';
    let target = end;
    if (time < start) {
      phase = 'upcoming';
      target = start;
    } else if (time >= end + 1000) {
      phase = 'ended';
      target = time;
    }

    if (countdown.dataset.phase !== phase) {
      countdown.dataset.phase = phase;
      countdown.querySelectorAll('[data-sale-label]').forEach((label) => {
        label.hidden = label.dataset.saleLabel !== phase;
      });
      const digits = countdown.querySelector('[data-sale-digits]');
      if (digits) digits.hidden = phase === 'ended';
    }

    const left = Math.max(0, Math.floor((target - time) / 1000));
    const values = {
      d: Math.floor(left / 86400),
      h: Math.floor(left / 3600) % 24,
      m: Math.floor(left / 60) % 60,
      s: left % 60,
    };
    countdown.querySelectorAll('[data-sale-unit]').forEach((unit) => {
      const text = pad(values[unit.dataset.saleUnit]);
      if (unit.textContent !== text) unit.textContent = text;
    });
  }

  function reloadIfDue(time) {
    if (designMode || reloading) return;
    let due = null;
    document.querySelectorAll('template[data-sale-pending]').forEach((marker) => {
      const opens = ms(marker.dataset.salePending);
      if (opens !== null && time >= opens && (due === null || opens > due)) due = opens;
    });
    if (due === null) return;

    const key = `sale-events:reloaded:${window.location.pathname}:${due}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, '1');
    } catch (error) {
      return; // Without storage nothing could stop a stale cached page reloading again.
    }
    reloading = true;
    // Spread the reloads over a few seconds rather than everyone on the same tick.
    window.setTimeout(() => window.location.reload(), Math.random() * 4000);
  }

  function update() {
    const time = now();
    document.querySelectorAll('[data-sale-from], [data-sale-to]').forEach((element) => {
      const open = isOpen(element, time);
      if (element.hasAttribute('data-sale-gate')) {
        element.toggleAttribute('data-sale-hide', !open);
        const state = element.querySelector('[data-sale-gate-state]');
        if (state) {
          const when = offset ? 'at the previewed time' : 'right now';
          state.textContent = open ? `Showing ${when}.` : `Hidden ${when}.`;
        }
      } else {
        const hide = !open && !element.hasAttribute('data-sale-reveal');
        if (element.hidden !== hide) element.hidden = hide;
      }
    });
    document.querySelectorAll('[data-sale-countdown]').forEach((countdown) => tick(countdown, time));
    reloadIfDue(time);
  }

  /* Theme editor only ------------------------------------------------------------- */

  function readPreview() {
    try {
      return window.sessionStorage.getItem(PREVIEW_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function setPreview(value) {
    try {
      if (value) window.sessionStorage.setItem(PREVIEW_KEY, value);
      else window.sessionStorage.removeItem(PREVIEW_KEY);
    } catch (error) {
      // The preview still works for this page without storage.
    }
    offset = value ? Number(value) - Date.now() : 0;
    update();
  }

  function renderPreviewBar() {
    const events = new Map();
    document.querySelectorAll('[data-sale-event][data-sale-start][data-sale-end]').forEach((root) => {
      const handle = root.dataset.saleEvent;
      const start = ms(root.dataset.saleStart);
      const end = ms(root.dataset.saleEnd);
      if (!handle || events.has(handle) || !start || !end) return;
      events.set(handle, { name: root.dataset.saleEventName || handle, start, end });
    });

    let bar = document.querySelector('.sale-preview-bar');
    if (!events.size) {
      if (bar) bar.remove();
      return;
    }
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'sale-preview-bar';
      const label = document.createElement('label');
      label.className = 'sale-preview-bar__label';
      label.textContent = 'Sale preview ';
      const select = document.createElement('select');
      select.className = 'sale-preview-bar__select';
      select.addEventListener('change', () => setPreview(select.value));
      label.append(select);
      const note = document.createElement('span');
      note.className = 'sale-preview-bar__note';
      note.textContent = 'Theme editor only';
      bar.append(label, note);
      document.body.append(bar);
    }

    const options = [new Option('Actual time', '')];
    Array.from(events.values())
      .sort((a, b) => a.start - b.start)
      .forEach(({ name, start, end }) => {
        options.push(new Option(`${name}: before it starts`, String(start - HOUR)));
        options.push(new Option(`${name}: live`, String(Math.min(start + HOUR, Math.floor((start + end) / 2)))));
        options.push(new Option(`${name}: after it ends`, String(end + HOUR)));
      });
    const select = bar.querySelector('select');
    select.replaceChildren(...options);
    select.value = readPreview();
    if (select.value !== readPreview()) setPreview(''); // That moment is no longer offered.
  }

  function revealBlock(target, on) {
    if (!(target instanceof Element)) return;
    const scheduled = target.closest('[data-sale-from], [data-sale-to]');
    if (scheduled) scheduled.toggleAttribute('data-sale-reveal', on);
    update();
  }

  function revealSection(section, on) {
    if (!(section instanceof Element)) return;
    const own = section.querySelector(':scope > [data-sale-from], :scope > [data-sale-to]');
    if (own) own.toggleAttribute('data-sale-reveal', on);

    // A Sale schedule section above that controls this one.
    let sibling = section.previousElementSibling;
    for (let distance = 1; sibling && distance <= 5; distance += 1) {
      const gate = sibling.querySelector(':scope > [data-sale-gate]');
      if (gate) {
        if (Number(gate.dataset.saleCount || 1) >= distance) gate.toggleAttribute('data-sale-reveal', on);
        break;
      }
      sibling = sibling.previousElementSibling;
    }
    update();
  }

  function init() {
    if (designMode) {
      const stored = readPreview();
      if (stored) offset = Number(stored) - Date.now();
      renderPreviewBar();

      const refresh = () => {
        renderPreviewBar();
        update();
      };
      document.addEventListener('shopify:section:load', refresh);
      document.addEventListener('shopify:section:unload', () => window.setTimeout(refresh));
      document.addEventListener('shopify:section:select', (event) => revealSection(event.target, true));
      document.addEventListener('shopify:section:deselect', (event) => revealSection(event.target, false));
      document.addEventListener('shopify:block:select', (event) => revealBlock(event.target, true));
      document.addEventListener('shopify:block:deselect', (event) => revealBlock(event.target, false));
    }

    update();
    window.setInterval(update, 1000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) update();
    });
  }

  window.saleEvents = { update, now };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
